import i18n from 'i18n';
import {
  ColDef, EditableCallbackParams, ITooltipParams, ValueFormatterParams,
} from '@ag-grid-enterprise/all-modules';
import { COMMON_GRID_COLUMN_FIELD_NAMES } from 'shared/commonGrid/grid-enums';
import { stateValueFormatter } from 'utils/valueFormatters';
import { DistributorConfigurationListenerAfWhiteList } from 'data/distributorConfiguration/distributorConfiguration.types';
import { getLocationList } from 'data/location/location.api';
import { Location } from 'data/location/location.types';
import {
  distributorConfigurationGroupFilterValueGetter,
  allowedPoiIpAddressesFilterValueGetter,
} from './utils/accessFunctionValueGetters';
import AllowedAccessFunctionIpCell from './components/DistributorConfigurationTable/AllowedAccessFunctionIpCell';
import GridGroupHeader from '../../components/GridGroupHeader';

export const NETWORK_ID_NOT_FOUND = 'NETWORK_ID_NOT_FOUND';

export enum AccessFunctionMenuActions {
  Copy = 'copy',
  Delete = 'delete',
  Edit = 'edit',
  View = 'view',
  Audit = 'audit',
  Reset = 'reset',
  Refresh = 'refresh',
}

export type AccessFunctionIpType = {
  allowedAfIps: string;
  listenerIp: string;
  listenerPort: string;
  name: string;
};

export type AllowedIpType = {
  poiName: string;
  poiIpAddress: string;
  actions: string;
};

/**
 * This value must be set in common-grid columnDefsVersion property. So, updating this value
 * forces common-grid to use the in code columnDefs instead of the saved grid setting.
 */
export const accessFunctionsColumnDefsVersion = '1';

export const accessFunctionsColumnDefs: ColDef[] = [
  {
    headerName: '',
    field: COMMON_GRID_COLUMN_FIELD_NAMES.Action,
    cellRenderer: 'actionMenuCellRenderer',
  },
  {
    headerName: i18n.t('accessFunction.grid.name'),
    field: 'name',
    rowGroup: true,
    hide: true,
    lockVisible: true,
    floatingFilter: true,
  },
  {
    headerName: i18n.t('accessFunction.grid.afType'),
    field: 'tagName',
    tooltipField: 'tagName',
    sortable: true,
    filter: 'agSetColumnFilter',
    floatingFilter: true,
    resizable: true,
    flex: 1,
    minWidth: 204,
    aggFunc: 'first',
    tooltipValueGetter: (params: Partial<ITooltipParams>): string => {
      if (params.node?.group) {
        return params.value;
      }

      return '';
    },
    cellRenderer: (params: Partial<ValueFormatterParams>): string => {
      if (params.node?.group) {
        return params.value;
      }

      return '';
    },
  },
  {
    headerName: i18n.t('accessFunction.grid.afId'),
    field: 'afid',
    sortable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    tooltipField: 'afid',
    resizable: true,
    flex: 1,
    minWidth: 150,
    aggFunc: 'first',
    tooltipValueGetter: (params: Partial<ITooltipParams>): string => {
      if (params.node?.group) {
        return params.value;
      }

      return '';
    },
    cellRenderer: (params: Partial<ValueFormatterParams>): string => {
      if (params.node?.group) {
        return params.value;
      }

      return '';
    },
  },
  {
    headerName: i18n.t('accessFunction.grid.status'),
    field: 'state',
    sortable: true,
    filter: 'agSetColumnFilter',
    floatingFilter: true,
    resizable: true,
    flex: 1,
    minWidth: 132,
    valueFormatter: stateValueFormatter,
    tooltipValueGetter: stateValueFormatter as any,
    cellRenderer: 'accessFunctionStatusCellRenderer',
  },
  {
    headerName: i18n.t('accessFunction.grid.interfaceType'),
    field: 'subType',
    sortable: true,
    filter: 'agSetColumnFilter',
    floatingFilter: true,
    resizable: true,
    tooltipField: 'subType',
    flex: 1,
    minWidth: 120,
  },
  {
    headerName: i18n.t('accessFunction.grid.afIpAddressURL'),
    field: 'ipAddress',
    tooltipField: 'ipAddress',
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    sortable: true,
    resizable: true,
    flex: 1,
    minWidth: 151,
    cellRenderer: 'notApplicableCellRenderer',
  },
  {
    headerName: i18n.t('accessFunction.grid.ipPort'),
    field: 'port',
    tooltipField: 'port',
    sortable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    resizable: true,
    flex: 1,
    minWidth: 97,
    cellRenderer: 'notApplicableCellRenderer',
  },
  {
    headerName: i18n.t('accessFunction.grid.destination'),
    field: 'destination',
    sortable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    resizable: true,
    flex: 1,
    minWidth: 178,
    tooltipValueGetter: (params: Partial<ITooltipParams>): string => {
      if (params.node?.group) {
        return '';
      }

      return params.data?.destinationToolTip;
    },
    cellRenderer: 'accessFunctionDestinationCellRenderer',
  },
  {
    headerName: i18n.t('accessFunction.grid.configuredState'),
    field: 'configuredState',
    sortable: true,
    filter: 'agSetColumnFilter',
    floatingFilter: true,
    resizable: true,
    flex: 1,
    minWidth: 200,
    valueFormatter: stateValueFormatter,
    tooltipValueGetter: stateValueFormatter as any,
    cellRenderer: 'accessFunctionConfiguredStatusCellRenderer',
  },
];

export const accessFunctionAutoGroupColumnDef: ColDef = {
  headerName: i18n.t('accessFunction.grid.name'),
  sortable: true,
  floatingFilter: true,
  filter: 'agTextColumnFilter',
  resizable: true,
  flex: 1,
  minWidth: 220,
  headerComponent: GridGroupHeader as any,
  filterValueGetter: (params: Partial<ValueFormatterParams>): string => {
    return params.data?.name;
  },
};

export const distributorConfigurationTableColumnDefs: ColDef[] = [
  {
    headerName: i18n.t('accessFunction.form.grid.ss8DistributorsConfiguration'),
    field: 'name',
    flex: 1,
    rowGroup: true,
    hide: true,
    filter: 'agTextColumnFilter',
  },
  {
    headerName: i18n.t('accessFunction.form.grid.listeningIp'),
    field: 'ipAddress',
    flex: 1,
    filter: 'agTextColumnFilter',
  },
  {
    headerName: i18n.t('accessFunction.form.grid.listeningPort'),
    field: 'port',
    flex: 1,
    filter: 'agTextColumnFilter',
  },
  {
    headerName: i18n.t('accessFunction.form.grid.allowedAfIpAddresses'),
    field: 'afWhitelistFormatted',
    flex: 1,
    cellRenderer: AllowedAccessFunctionIpCell as any,
    filter: 'agTextColumnFilter',
    filterValueGetter: allowedPoiIpAddressesFilterValueGetter,
  },
];

export const distributorConfigurationTableAutoGroupColumnDef: ColDef = {
  filter: 'agTextColumnFilter',
  filterValueGetter: distributorConfigurationGroupFilterValueGetter,
};

export const getCountryStateMap = (locations: Location[]) => {
  const stateListByCountry: {[country: string]: { stateName: string, stateCode: string }[]} = {};
  locations.forEach(({ country, stateName, stateCode }) => {
    if (stateListByCountry[country] == null) {
      stateListByCountry[country] = [];
    }
    stateListByCountry[country].push({ stateName, stateCode });
  });
  return stateListByCountry;
}

export const distributorsIPsWhiteListEditableTableColumnDefs: ColDef[] = [
  {
    headerName: i18n.t('allowedIps.grid.afName'),
    field: 'afName',
    flex: 2,
    filter: 'agTextColumnFilter',
    editable: true,
    cellRenderer: 'ipAFNameCellRenderer',
  },
  {
    headerName: i18n.t('allowedIps.grid.afIpAddress'),
    field: 'afAddress',
    flex: 2,
    filter: 'agTextColumnFilter',
    editable: true,
    cellRenderer: 'ipAddressCellRenderer',
  },
  {
    headerName: i18n.t('allowedIps.grid.ingressLB'),
    field: 'isGrayItem',
    flex: 2,
    cellRenderer: 'checkboxCellRenderer',
  },
  {
    headerName: i18n.t('allowedIps.grid.country'),
    field: 'country',
    flex: 2,
    editable: true,
    filter: 'agTextColumnFilter',
    // cellEditor: 'agRichSelectCellEditor',
    // cellEditorParams: (params: any) => {
    //   // const response = await getLocationList();
    //   // const map = getCountryStateMap(response.data.data as Location[]);
    //   // return {
    //   //   values: Object.keys(map)
    //   // };
    //   getLocationList().then((resp) => {
    //     if (resp.data.data) {
    //       const map = getCountryStateMap(resp.data.data as Location[]);
    //       // return {
    //       //   values: Object.keys(map)
    //       // };
    //       return Object.keys(map);
    //     }
    //   });
    //   // return {
    //   //   values: []
    //   // };
    // },
    // cellEditorPopup: true
  },
  {
    headerName: i18n.t('allowedIps.grid.stateName'),
    field: 'stateName',
    flex: 2,
    editable: true,
    // editable: (params: EditableCallbackParams) => {
    //   return !!params.data.country;
    // },
    filter: 'agTextColumnFilter',
    // cellEditor: 'agRichSelectCellEditor',
    // cellEditorParams: {
    //   values: ['English', 'Spanish', 'French', 'Portuguese', '(other)']
    // },
    // cellEditorPopup: true
  },
  {
    headerName: i18n.t('allowedIps.grid.city'),
    field: 'city',
    flex: 2,
    editable: true,
    // editable: (params: EditableCallbackParams) => {
    //   return !!params.data.stateName;
    // },
    filter: 'agTextColumnFilter',
    // cellEditor: 'agRichSelectCellEditor',
    // cellEditorParams: {
    //   values: ['English', 'Spanish', 'French', 'Portuguese', '(other)'],
    // },
    // cellEditorPopup: true
  },
  {
    headerName: i18n.t('allowedIps.grid.actions'),
    field: 'actions',
    filter: false,
    cellClass: 'allowed-ips-update-table-center-cell',
    headerClass: 'allowed-ips-update-table-action-header',
    cellRenderer: 'actionCellRenderer',
  },
];

export const distributorsIPsWhiteListReadOnlyTableColumnDefs: ColDef[] = [
  {
    headerName: i18n.t('allowedIps.grid.afName'),
    field: 'afName',
    flex: 2,
    filter: 'agTextColumnFilter',
  },
  {
    headerName: i18n.t('allowedIps.grid.afIpAddress'),
    field: 'afAddress',
    flex: 2,
    filter: 'agTextColumnFilter',
  },
  {
    headerName: i18n.t('allowedIps.grid.ingressLB'),
    field: 'isGrayItem',
    flex: 2,
    filter: 'agTextColumnFilter',
    valueGetter: (p) => {
      if (p.data.isGrayItem === true) {
        return '\u2713';
      }
      return '';
    }
  },
  {
    headerName: i18n.t('allowedIps.grid.city'),
    field: 'city',
    flex: 2,
    filter: 'agTextColumnFilter',
  },
  {
    headerName: i18n.t('allowedIps.grid.stateName'),
    field: 'stateName',
    flex: 2,
    filter: 'agTextColumnFilter',
  },
  {
    headerName: i18n.t('allowedIps.grid.country'),
    field: 'country',
    flex: 2,
    filter: 'agTextColumnFilter',
  },
];

export type AFWhiteListVersioned = {
  rowKey: number;
  actions?: string;
} & DistributorConfigurationListenerAfWhiteList;

export const accessFunctionsAllowedWithoutInterfaces: {[key:string]: boolean} = {
  DRVIRTUAL: true,
  PVOIP: true,
};
